import { useState, lazy, Suspense } from "react";
import {
  Box,
  Typography,
  Stack,
  TextField,
  Grid,
  Chip,
  IconButton,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import PlaceIcon from "@mui/icons-material/Place";
import DeleteIcon from "@mui/icons-material/Delete";

import { canalLabel } from "./pipelineUtils";

const LocationPicker = lazy(() => import("./LocationPicker"));

export default function ConfigGeneral({ store }) {
  const [nuevoCodigo, setNuevoCodigo] = useState("");
  const [nuevaZona, setNuevaZona] = useState("");
  const [nuevoCanal, setNuevoCanal] = useState("");

  const [showMap, setShowMap] = useState(false);
  const ubicacion = store.config.ubicacionNegocio;

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={8}>
          <TextField
            label="Nombre del negocio"
            value={store.config.nombreNegocio}
            onChange={(e) => store.actualizarConfig({ nombreNegocio: e.target.value })}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="Moneda"
            value={store.config.moneda}
            onChange={(e) => store.actualizarConfig({ moneda: e.target.value })}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="Código de país"
            value={store.config.codigoPais || "591"}
            onChange={(e) => store.actualizarConfig({ codigoPais: e.target.value.replace(/\D/g, "") })}
            fullWidth
            size="small"
            placeholder="591"
            helperText="Para WhatsApp"
          />
        </Grid>
        <Grid item xs={8}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
            Códigos adicionales
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5} alignItems="center">
            {(store.config.codigosPaisExtra || []).map((cod) => (
              <Chip
                key={cod}
                label={`+${cod}`}
                size="small"
                onDelete={() => store.actualizarConfig({
                  codigosPaisExtra: (store.config.codigosPaisExtra || []).filter(c => c !== cod)
                })}
              />
            ))}
            <Stack direction="row" spacing={0.5} alignItems="center">
              <TextField
                size="small"
                placeholder="Ej: 54"
                value={nuevoCodigo}
                onChange={(e) => setNuevoCodigo(e.target.value.replace(/\D/g, ""))}
                sx={{ width: 80 }}
                inputProps={{ maxLength: 4 }}
              />
              <IconButton
                size="small"
                onClick={() => {
                  if (!nuevoCodigo.trim()) return;
                  const extras = store.config.codigosPaisExtra || [];
                  if (!extras.includes(nuevoCodigo) && nuevoCodigo !== store.config.codigoPais) {
                    store.actualizarConfig({ codigosPaisExtra: [...extras, nuevoCodigo] });
                  }
                  setNuevoCodigo("");
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Días entre seguimiento"
            value={store.config.diasEntreSeguimiento ?? 7}
            onChange={(e) => store.actualizarConfig({ diasEntreSeguimiento: parseInt(e.target.value) || 0 })}
            type="number"
            size="small"
            sx={{ width: 180 }}
            helperText="Auto-agendar revisita (0 = desactivado)"
            inputProps={{ min: 0 }}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
            Canales de adquisición
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5} alignItems="center">
            {(store.config.canales || []).map((canal) => (
              <Chip
                key={canal}
                label={canalLabel(canal)}
                size="small"
                onDelete={
                  (store.config.canales || []).length <= 1
                    ? undefined
                    : () => store.actualizarConfig({
                        canales: (store.config.canales || []).filter((c) => c !== canal),
                      })
                }
              />
            ))}
            <Stack direction="row" spacing={0.5} alignItems="center">
              <TextField
                size="small"
                placeholder="Ej: tiktok"
                value={nuevoCanal}
                onChange={(e) => setNuevoCanal(e.target.value)}
                sx={{ width: 140 }}
              />
              <IconButton
                size="small"
                onClick={() => {
                  const texto = nuevoCanal.trim();
                  if (!texto) return;
                  store.agregarCanalesSiFaltan([texto]);
                  setNuevoCanal("");
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
            Aparecen al crear leads y en la importación masiva. También se agregan solos si vienen en el CSV.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
            Zonas geográficas
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5} alignItems="center">
            {(store.config.zonas || []).map((zona) => (
              <Chip
                key={zona}
                label={zona}
                size="small"
                onDelete={() => store.actualizarConfig({
                  zonas: (store.config.zonas || []).filter(z => z !== zona)
                })}
              />
            ))}
            <Stack direction="row" spacing={0.5} alignItems="center">
              <TextField
                size="small"
                placeholder="Ej: Norte"
                value={nuevaZona}
                onChange={(e) => setNuevaZona(e.target.value)}
                sx={{ width: 120 }}
              />
              <IconButton
                size="small"
                onClick={() => {
                  if (!nuevaZona.trim()) return;
                  const zonas = store.config.zonas || [];
                  if (!zonas.includes(nuevaZona.trim())) {
                    store.actualizarConfig({ zonas: [...zonas, nuevaZona.trim()] });
                  }
                  setNuevaZona("");
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
            <PlaceIcon sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }} />
            Ubicación del negocio
          </Typography>
          {ubicacion ? (
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  icon={<PlaceIcon />}
                  label={`${ubicacion.lat.toFixed(5)}, ${ubicacion.lng.toFixed(5)}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => store.actualizarConfig({ ubicacionNegocio: null })}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Button size="small" onClick={() => setShowMap(p => !p)} sx={{ alignSelf: "flex-start", textTransform: "none" }}>
                {showMap ? "Ocultar mapa" : "Cambiar ubicación"}
              </Button>
              {showMap && (
                <Suspense fallback={<Box sx={{ height: 180, bgcolor: "grey.100", borderRadius: 2 }} />}>
                  <LocationPicker
                    value={ubicacion}
                    onChange={(loc) => store.actualizarConfig({ ubicacionNegocio: loc })}
                    height={180}
                  />
                </Suspense>
              )}
            </Stack>
          ) : (
            <Stack spacing={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<MyLocationIcon />}
                onClick={() => {
                  if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                      (pos) => {
                        store.actualizarConfig({
                          ubicacionNegocio: { lat: pos.coords.latitude, lng: pos.coords.longitude },
                        });
                      },
                      () => setShowMap(true)
                    );
                  } else {
                    setShowMap(true);
                  }
                }}
                sx={{ alignSelf: "flex-start", textTransform: "none" }}
              >
                Usar mi ubicación
              </Button>
              {!showMap && (
                <Button size="small" onClick={() => setShowMap(true)} sx={{ alignSelf: "flex-start", textTransform: "none" }}>
                  Elegir en el mapa
                </Button>
              )}
              {showMap && (
                <Suspense fallback={<Box sx={{ height: 180, bgcolor: "grey.100", borderRadius: 2 }} />}>
                  <LocationPicker
                    value={null}
                    onChange={(loc) => store.actualizarConfig({ ubicacionNegocio: loc })}
                    height={180}
                  />
                </Suspense>
              )}
            </Stack>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
            El mapa de leads iniciará en esta ubicación
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
}
